import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, HelpCircle, Mail, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_META = {
  "help_center": { icon: HelpCircle, color: "text-blue-500", bg: "bg-blue-500/10", heroImage: "https://customer-assets.emergentagent.com/job_596d7fd9-5b71-4e0a-b52b-9733ffe4b361/artifacts/3awsa0xh_WhatsApp%20Image%202026-02-14%20at%202.21.30%20PM.jpeg" },
  "contact_us": { icon: Mail, color: "text-accent", bg: "bg-accent/10", heroImage: "https://customer-assets.emergentagent.com/job_596d7fd9-5b71-4e0a-b52b-9733ffe4b361/artifacts/wvehreua_image.png" },
  "refund_policy": { icon: ShieldCheck, color: "text-emerald-500", bg: "bg-emerald-500/10", heroImage: "https://customer-assets.emergentagent.com/job_596d7fd9-5b71-4e0a-b52b-9733ffe4b361/artifacts/w84n14u4_WhatsApp%20Image%202026-02-14%20at%202.32.37%20PM.jpeg" },
  "about_us": { icon: HelpCircle, color: "text-indigo-500", bg: "bg-indigo-500/10", heroImage: "https://customer-assets.emergentagent.com/job_596d7fd9-5b71-4e0a-b52b-9733ffe4b361/artifacts/rp42unuo_WhatsApp%20Image%202026-02-20%20at%2012.02.11%20PM.jpeg" },
};

function renderMarkdown(text) {
  return text.split("\n").map((line, i) => {
    if (line.startsWith("**") && line.endsWith("**")) {
      return <h3 key={i} className="font-bold text-navy-900 text-lg mt-6 mb-2">{line.replace(/\*\*/g, "")}</h3>;
    }
    if (line.includes("**")) {
      const parts = line.split(/\*\*/);
      return (
        <p key={i} className="text-gray-600 leading-relaxed mb-1">
          {parts.map((part, j) => j % 2 === 1 ? <strong key={j} className="text-navy-900">{part}</strong> : part)}
        </p>
      );
    }
    if (line.startsWith("- ")) {
      return <li key={i} className="text-gray-600 ml-4 list-disc leading-relaxed">{line.slice(2)}</li>;
    }
    if (line.trim() === "") return <div key={i} className="h-3" />;
    return <p key={i} className="text-gray-600 leading-relaxed mb-1">{line}</p>;
  });
}

export default function SupportPage() {
  const { pageKey } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  const meta = PAGE_META[pageKey] || PAGE_META.help_center;
  const Icon = meta.icon;

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/site-settings/page/${pageKey}`)
      .then(res => setPage(res.data))
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [pageKey]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid={`support-page-${pageKey}`}>
      <section className="bg-navy-900 py-16 relative overflow-hidden">
        {meta.heroImage && (
          <img src={meta.heroImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className={`inline-flex items-center gap-2 ${meta.bg} backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-6`}>
              <Icon className={`w-4 h-4 ${meta.color}`} />
              <span className="text-sm text-white/90 font-medium">Support</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="support-page-title">
              {loading ? "Loading..." : page?.title || "Page Not Found"}
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : page ? (
          <motion.div
            className="bg-white rounded-2xl border border-gray-100 p-8 sm:p-10 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            data-testid="support-page-content"
          >
            {renderMarkdown(page.content)}
          </motion.div>
        ) : (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg mb-4">This page doesn't exist yet.</p>
            <Link to="/" className="text-accent hover:underline font-medium">Go back home</Link>
          </div>
        )}
      </section>
    </div>
  );
}
